import { Injectable, NgZone, inject } from '@angular/core';

import { ConnectionConfig } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { PageViewDataProvider, TrackingData } from '@frontend/vanilla/core';
import { meanBy } from 'lodash-es';
import { Observable, Subject, filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NetworkPerformancePageViewDataProvider implements PageViewDataProvider {
    private connectionConfig = inject(ConnectionConfig);
    private routerEventService = inject(RouterEventsService);

    private firstLoad = true;
    private tracked = new Subject<TrackingData>();
    private zone = inject(NgZone);

    constructor() {
        this.routerEventService.currentActivationEnd.pipe(filter(isDefined)).subscribe(() => {
            this.measure();
        });
    }

    getData(): Observable<TrackingData> {
        return this.tracked.asObservable();
    }

    private measure(): void {
        if (!performance || !performance.timing) {
            return;
        }

        const cdsUrl = this.connectionConfig.cdsFrontendUrl ?? this.connectionConfig.cdsUrlBase;
        const cdsEntries = performance.getEntriesByType('resource').filter((e) => new RegExp(cdsUrl).test(e.name));

        if (!cdsEntries || !cdsEntries.length) {
            return;
        }

        // clear the resource timings in case we have navigated before (meaning this is not the first time on the homepage)
        // otherwise this would break the tracking from the homepage first load
        if (!this.firstLoad) {
            performance.clearResourceTimings();
        }

        this.firstLoad = false;

        this.zone.runOutsideAngular(() =>
            setTimeout(() => {
                this.tracked.next({
                    'performance.cds': this.round(meanBy(cdsEntries, (e) => e.duration)),
                });
            }),
        );
    }

    /**
     * Rounds the specified number or returns `defaultValue` if it's null, undefined or invalid.
     *
     * @experimental
     */
    private round(value: number | null | undefined, defaultValue = 0): number {
        const rounded = Math.round(value ?? 0);

        return Number.isNaN(rounded) ? defaultValue : rounded;
    }
}
